import React from 'react';
import mammoth from 'mammoth/mammoth.browser';
import DOMPurify from 'dompurify';
import Modal from '../../commonComponents/util/Modal';
import LoadingSpinner from '../../commonComponents/util/LoadingSpinner';
import { formatBankInfoDisplay } from '../../../lib/utils';

const DOCX_MIME = "application/vnd.openxmlformats-officedocument.wordprocessingml.document";

export default class ApplicationAttachmentModal extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            attachmentSource: '',
            attachmentType: '',
            docxHtml: '',
            docxLoading: false,
            docxError: false,
            zoom: true
        }
    }

    componentWillReceiveProps(nextProps) {
        if (nextProps.document.source !== this.props.document.source) {
            let attachmentType;
            switch ((nextProps.document.extension || '').toLowerCase()) {
                case ".pdf":
                    attachmentType = "application/pdf";
                    break;
                case ".html":
                    attachmentType = "text/html";
                    break;
                case ".png":
                    attachmentType = "image/png";
                    break;
                case ".jpeg":
                case ".jpg":
                    attachmentType = "image/jpeg";
                    break;
                case ".tif":
                case ".tiff":
                    attachmentType = "image/tiff";
                    break;
                case ".gif":
                    attachmentType = "image/gif";
                    break;
                case ".bmp":
                    attachmentType = "image/bmp";
                    break;
                case ".docx":
                    attachmentType = DOCX_MIME;
                    break;
                default:
                    attachmentType = "";
                    break;
            }

            this.revokeAttachmentSource();

            if (attachmentType === DOCX_MIME) {
                this.setState({
                    attachmentSource: '',
                    attachmentType,
                    docxHtml: '',
                    docxError: false,
                    docxLoading: true,
                    zoom: true
                });
                this.renderDocx(nextProps.document.source);
            } else {
                const hasValidSource = nextProps.document.source && attachmentType;
                this.setState({
                    attachmentSource: hasValidSource
                        ? URL.createObjectURL(new Blob([nextProps.document.source], { type: attachmentType }))
                        : '',
                    attachmentType,
                    docxHtml: '',
                    docxError: false,
                    docxLoading: false,
                    zoom: true
                });
            }
        }
    }

    componentWillUnmount() {
        this.revokeAttachmentSource();
    }

    revokeAttachmentSource = () => {
        if (this.state.attachmentSource) {
            URL.revokeObjectURL(this.state.attachmentSource);
        }
    }

    renderDocx = async (source) => {
        try {
            if (!source) {
                this.setState({ docxLoading: false });
                return;
            }
            // mammoth expects an ArrayBuffer; the attachment is fetched as arraybuffer.
            const arrayBuffer = source instanceof ArrayBuffer ? source : new Uint8Array(source).buffer;
            const result = await mammoth.convertToHtml({ arrayBuffer });
            // Sanitize the generated markup before rendering to guard against any
            // malicious content embedded in the uploaded document.
            const safeHtml = DOMPurify.sanitize(result.value, { USE_PROFILES: { html: true } });
            this.setState({ docxHtml: safeHtml, docxLoading: false, docxError: false });
        } catch (e) {
            this.setState({ docxLoading: false, docxError: true });
        }
    }

    toggleZoom = () => {
        this.setState({ zoom: !this.state.zoom });
    }

    renderDocxPreview() {
        if (this.state.docxLoading) {
            return <LoadingSpinner height="30" />;
        }
        if (this.state.docxError) {
            return (<div className="small-text text-center"><i className="icon ion-paperclip icon-left xl-text middle"></i>
                Preview is not available for this document.<br />View the file in your downloads.</div>);
        }
        return (
            <div className="docx-preview-container">
                <div className="docx-preview-page"
                    dangerouslySetInnerHTML={{ __html: this.state.docxHtml }} />
            </div>
        );
    }

    render() {
        //TODO - Chrome Warning - Resource interpreted as Document but transferred with MIME type image/tiff...  
        //Ignoring for now.  If this becomes an issue may need to return documents with specific headers from backend.  

        const { recipient } = this.props;
        const primaryBankAccount = (recipient && recipient.bankAccounts)
            ? recipient.bankAccounts.filter(acct => acct.active)[0]
            : null;
        
        const isReceipt = this.props.documentType === 'receipt';
        const documentLabel = isReceipt ? 'Receipt' : 'Attachment';
        const isDocx = this.state.attachmentType === DOCX_MIME;

        return (
            <Modal modalShowing={this.props.show}>
                <div className="modal-container">
                    <div className={`modal attachment-modal${this.state.zoom ? "-lg" : ""}`}>
                        <div className="modal-header">
                            {!this.props.success ? <h4 className="page-header">{documentLabel}</h4> : <h4 className="page-header">{this.props.document.name}
                                <i onClick={this.toggleZoom} className="material-icons input" style={{ marginLeft: '16px' }}>{this.state.zoom ? 'zoom_out' : 'zoom_in'}</i></h4>}
                            <i className="icon ion-ios-close-empty" onClick={this.props.close} />
                        </div>
                        {!!recipient &&
                                <p>
                                    {recipient.displayName && `Recipient ${recipient.displayName}`}
                                    {recipient.teudatZahut && ` | Teudat Zehut ${recipient.teudatZahut}`}
                                    {primaryBankAccount && ` | Bank Account ${formatBankInfoDisplay(primaryBankAccount.bankRoutingNumber, primaryBankAccount.bankBranchNumber, primaryBankAccount.bankAccountNumber, primaryBankAccount.bankAccountHolder)}`}
                                </p>}
                        {this.props.loading ? <LoadingSpinner height="30" />
                            : (!this.props.success
                                ? <div className="small-text text-center"><i className="icon ion-paperclip icon-left xl-text middle"></i>Could Not Load {documentLabel}</div>
                                : (isDocx
                                    ? this.renderDocxPreview()
                                    : (this.state.attachmentSource &&
                                        <div>
                                            {(this.state.attachmentType === "image/tiff") && <div className="small-text text-center"><i className="icon ion-paperclip icon-left xl-text middle"></i>
                                                Preview is not compatible with the file format of this attachment.<br />View the file in your downloads.</div>}
                                            <iframe title="app-attachment" style={{ height: 1000, width: 1000 }} src={`${this.state.attachmentSource}#zoom=135`}></iframe>
                                        </div>
                                    )
                                ))}
                    </div>
                </div>
            </Modal>
        );
    }
}


// WEBPACK FOOTER //
// ./src/components/recipientAccountsComponents/recipientAccountDetails/ApplicationAttachmentModal.jsx